import { Button, cn, Divider, Spinner, Stack } from '@landler/component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RoadmapNode, RoadmapNodeStatus } from '@/api/rest/resources/types/organization';
import { withSuspenseBoundary } from '@/components/SuspenseBoundary';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { useIsStaffUser } from '@/hooks/useIsStaffUser';
import { useUserRole } from '@/hooks/useUserRole';
import { isNullish } from '@/utils/booleans';
import { getStyleForRoadmapStatus } from '@/utils/getStyleForRoadmap';

import { useOrganizationId } from '../../hooks/useOrganizationId';
import { useOrganizationRoadmapNodes } from '../../hooks/useOrganizationRoadmapNodes';
import { DeleteRoadmapNodeWithConfirmation } from './DeleteRoadmapNodeWithConfirmation';
import { EditRoadmapNode, MarkNodeAsDone } from './EditRoadmapNode';

export const Node = withSuspenseBoundary(
  ({ roadmapId, showTimeline = false }: { roadmapId: string; showTimeline?: boolean }) => {
    const { t } = useTranslation();
    const organizationId = useOrganizationId();
    const isStaffUser = useIsStaffUser();
    const { isAdmin } = useUserRole();

    const roadmap = useOrganizationRoadmapNodes(undefined, { roadmap_node_id: roadmapId }).data.at(0);
    if (isNullish(roadmap)) {
      throw new Error(`Roadmap ${roadmapId} not found`);
    }

    const [showAddRoadmap, setShowAddRoadmap] = useState<boolean>(false);
    const [roadmapForEdit, setRoadmapForEdit] = useState<RoadmapNode | null>(null);
    const [roadmapForDelete, setRoadmapForDelete] = useState<RoadmapNode | null>(null);

    const [openChildren, setOpenChildren] = useState<boolean>(false);
    const hasChildren = !isNullish(roadmap.children) && roadmap.children.length > 0;

    const statusStyle = getStyleForRoadmapStatus(roadmap.status);
    const doneStatusStyle = getStyleForRoadmapStatus(RoadmapNodeStatus.closed);
    const createdStatusStyle = getStyleForRoadmapStatus(RoadmapNodeStatus.created);

    const disabledTextStyle = !roadmap.visible_to_customer ? 'text-text-disabled' : '';

    if (!isStaffUser && !roadmap.visible_to_customer) return null;

    return (
      <>
        <Stack
          className={cn('gap-6 flex flex-row items-stretch w-full group/node', hasChildren && 'cursor-pointer')}
          onClick={(e) => {
            e.stopPropagation();
            setOpenChildren(!openChildren);
          }}
        >
          <Stack className='flex flex-col items-start'>
            {showTimeline && (
              <Icon
                icon='ri-circle-fill'
                style={{
                  color: statusStyle.color,
                }}
              />
            )}
            <Divider
              direction='vertical'
              className='border-l-0 w-1 flex-1 min-h-20 self-center'
              style={{
                backgroundColor:
                  roadmap.status === RoadmapNodeStatus.closed ? doneStatusStyle.color : createdStatusStyle.color,
              }}
            />
          </Stack>

          <Stack className='gap-2 pt-1 w-full pb-3'>
            <Stack className='justify-between md:flex-row md:items-center flex flex-col'>
              <Stack className='gap-2 my-4'>
                {/* <span className='typography-overline text-text-secondary'>{roadmap.status}</span> */}
                <span className={cn('typography-h4 whitespace-normal', disabledTextStyle)}>{roadmap.title}</span>
                <span className={cn('typography-body1 text-text-secondary whitespace-normal', disabledTextStyle)}>
                  {roadmap.description}
                </span>
              </Stack>
              <Stack className='flex flex-row gap-4 invisible group-hover/node:visible'>
                {isStaffUser && (
                  <Button
                    data-testid='add-roadmap-button'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAddRoadmap(!showAddRoadmap);
                    }}
                    variant='outline'
                    leftAdornment={<Icon icon={showAddRoadmap ? 'ri-subtract-line' : 'ri-add-large-line'} />}
                    className='px-3 py-3 rounded-3xl border border-neutral-black-12'
                  />
                )}
                <Button
                  data-testid='edit-roadmap-button'
                  onClick={(e) => {
                    e.stopPropagation();
                    setRoadmapForEdit(roadmap);
                  }}
                  variant='outline'
                  leftAdornment={<Icon icon='ri-pencil-line' />}
                  className='px-3 py-3 rounded-3xl border border-neutral-black-12'
                />
                <Button
                  data-testid='delete-roadmap-button'
                  onClick={(e) => {
                    e.stopPropagation();
                    setRoadmapForDelete(roadmap);
                  }}
                  variant='outline'
                  leftAdornment={<Icon icon='ri-delete-bin-line' />}
                  className='px-3 py-3 rounded-3xl border border-neutral-black-12'
                />
              </Stack>
            </Stack>
            <MarkNodeAsDone className={disabledTextStyle} organizationId={organizationId} roadmapNode={roadmap} />
            <Stack className='gap-0.5 w-full'>
              {hasChildren && openChildren && (
                <Stack className='py-1' data-testid='roadmap-node-children'>
                  {roadmap.children?.map(
                    (child) => !isNullish(child.id) && <Node key={child.id} roadmapId={child.id} />,
                  )}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
        <DeleteRoadmapNodeWithConfirmation
          roadmapNode={roadmapForDelete}
          onClose={() => {
            setRoadmapForDelete(null);
            setOpenChildren(openChildren);
          }}
        />
        {isStaffUser && showAddRoadmap && (
          <EditRoadmapNode
            organizationId={organizationId}
            parentId={roadmap.id}
            onSuccess={() => setShowAddRoadmap(false)}
            onClose={() => setShowAddRoadmap(false)}
            title={t('shared.goals.editNode.addMilestone')}
          />
        )}
        {(isAdmin || isStaffUser) && !isNullish(roadmapForEdit) && (
          <EditRoadmapNode
            organizationId={organizationId}
            roadmapNode={roadmapForEdit}
            onSuccess={() => setRoadmapForEdit(null)}
            onClose={() => setRoadmapForEdit(null)}
            showFullForm={!isAdmin}
          />
        )}
      </>
    );
  },
  <Stack centerMain>
    <Spinner size={4} />
  </Stack>,
);
